import React, { useMemo, useState, useRef, useEffect } from 'react';
import { User } from '../../types';
import { BADGES } from '../../services/gamification';
import { Sparkles } from 'lucide-react';

interface UserAvatarProps {
  user?: User; // Direct user object (preferred)
  userId?: string; // OR Lookup by ID
  users?: User[]; // Required if using userId lookup
  src?: string; // Fallback or override
  alt?: string;
  className?: string; // Class for the IMG element
  wrapperClassName?: string; // Class for the wrapper
  showSparkle?: boolean;
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  userId,
  users,
  src,
  alt,
  className = '',
  wrapperClassName = '',
  showSparkle = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Intersection Observer for robust lazy loading
  useEffect(() => {
    // If browser doesn't support IntersectionObserver, just load immediately
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      });
    }, {
      rootMargin: '100px', // Start loading 100px before it comes into view
    });

    // Observe element
    if (containerRef.current) observer.observe(containerRef.current);
    else if (imgRef.current) observer.observe(imgRef.current);

    return () => observer.disconnect();
  }, []);

  // Resolve User Object
  const resolvedUser = useMemo(() => {
    if (user) return user;
    if (userId && users) return users.find((u) => u.id === userId);
    return null;
  }, [user, userId, users]);

  // Determine Image Source
  const imageSrc = src || resolvedUser?.avatar || 'https://via.placeholder.com/150';
  const imageAlt = alt || resolvedUser?.name || 'User Avatar';

  // Check for All Badges
  const hasAllBadges = useMemo(() => {
    // TEMP: Force true for testing purposes as requested
    // return resolvedUser?.email === "thai.nguyen@techcoop.vn";

    // Original Logic
    if (!resolvedUser || !resolvedUser.badgesEarnedOn) return false;
    const earnedCount = Object.keys(resolvedUser.badgesEarnedOn).length;
    return earnedCount >= BADGES.length;
  }, [resolvedUser]);

  const shouldSparkle = showSparkle && hasAllBadges;

  // Helper to render content (Placeholder or Image)
  const renderContent = () => {
    if (!isVisible) {
      return (
        <div
          ref={!shouldSparkle ? containerRef : undefined}
          className={`${className} bg-slate-200 animate-pulse`}
        />
      );
    }
    return (
      <img
        ref={!shouldSparkle ? imgRef : undefined}
        src={imageSrc}
        alt={imageAlt}
        className={shouldSparkle ? `${className} relative z-10` : className}
        style={shouldSparkle ? { margin: 0 } : undefined}
        loading="lazy"
      />
    );
  };

  if (shouldSparkle) {
    return (
      <div
        ref={containerRef}
        className={`avatar-sparkle-wrapper ${wrapperClassName} inline-block relative shrink-0 h-fit`}
      >
        {renderContent()}
        {/* Specular Highlight Ring */}
        <div className="ring-highlight"></div>
        <Sparkles
          size={12}
          className="sparkle-icon absolute -top-1 -right-1 z-20 text-yellow-300 fill-yellow-300 animate-pulse"
          style={{
            filter: 'drop-shadow(0 0 2px rgba(255, 215, 0, 0.8))'
          }}
        />
      </div>
    );
  }

  return renderContent();
};

export default React.memo(UserAvatar);
